import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, getShopMscBalance } from "../utils/api";
import "../styles/shopEarnings.css";
import ShopStaffCredit from "../components/ShopStaffCredit";

export default function ShopEarnings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [txFilter, setTxFilter] = useState("all");
  const [openTxId, setOpenTxId] = useState(null);
  const [msc, setMsc] = useState({ balanceMsc: 0, balanceFiat: 0, currency: "USD", fxRate: 1 });
  const [mscLoaded, setMscLoaded] = useState(false);

  const loadEarnings = async () => {
    setLoading(true);
    setError("");
    try {
      // ✅ Fetch from /api/shop/earnings (std route)
      const res = await apiGet("/shop/earnings");
      setData(res);
    } catch (err) {
      setError(err?.message || "Failed to load earnings");
    } finally {
      setLoading(false);
    }
  };

  const loadMsc = async () => {
    try {
      const b = await getShopMscBalance();
      setMsc({
        balanceMsc: Number(b?.balanceMsc || 0),
        balanceFiat: Number(b?.balanceFiat || 0),
        currency: b?.currency || "USD",
        fxRate: Number(b?.fxRate || 1),
      });
      setMscLoaded(true);
    } catch {
      // non-fatal: leave MSC at 0 if the balance endpoint fails
    }
  };

  useEffect(() => {
    loadEarnings();
    loadMsc();
  }, []);

  // ✅ Map backend response (see backend/routes/shopEarningsRoutes.js)
  // Backend returns: { grossSales, todaySales, monthSales, breakdown: { pending, paid, shipped, completed }, recent: [], totalOrders }
  
  const currency = data?.currency || "USD";
  // Headline = NET earnings (what the shop actually receives), with gross shown beneath.
  const total = data?.netSales ?? data?.grossSales ?? 0;
  const grossSales = data?.grossSales || 0;
  const todaySales = data?.todaySales || 0;
  const monthSales = data?.monthSales || 0;
  const totalOrders = data?.totalOrders || 0;
  
  // "Pending" = orders not yet shipped/completed
  const pending = (data?.breakdown?.pending || 0) + (data?.breakdown?.paid || 0);
  
  // "Balance" (Available) = shipped/completed orders (rough approx)
  const balance = (data?.breakdown?.shipped || 0) + (data?.breakdown?.completed || 0);

  // Breakdown by status
  const breakdown = data?.breakdown || {};

  // Multi-currency: other currencies beyond the primary
  const otherCurrencies = (data?.totalsByCurrency || []).filter(
    (c) => c.currency !== currency && c.grossSales > 0
  );

  // Transactions list
  const rawTransactions = Array.isArray(data?.recent) ? data.recent : [];
      const transactions = rawTransactions.map(t => ({
      _id: t.id,
      type: `Order #${String(t.id).slice(-4)}`,
      amount: (t.net ?? t.total),
      gross: t.total,
      createdAt: t.createdAt,
      status: t.status,
        returnStatus: t.returnStatus || "none",
        returnId: t.returnId || null,
        refundedAt: t.refundedAt || null,
      itemsCount: t.itemsCount,
      currency: t.currency || currency,
      customerName: t.buyerName || t.customerName || t.userName || t.name || "",
      customerEmail: t.buyerEmail || t.customerEmail || "",
      raw: t
  }));

  const filteredTransactions = transactions.filter((t) => {
    if (txFilter === "all") return true;
    return String(t.status || "").toLowerCase() === txFilter;
  });

  return (
    <div style={S.wrap} className="page-responsive se-page">
       <div style={S.header} className="se-header">
         <div style={S.h1}>Earnings</div>
         <div style={{ display: "flex", gap: 10 }}>
           <button style={S.btnRefresh} onClick={loadEarnings} disabled={loading}>
             {loading ? "Refreshing..." : "Refresh"}
           </button>
           <button style={S.btnWithdraw} onClick={() => nav("/shop/withdrawal")}>
             💸 Withdraw
           </button>
         </div>
       </div>

       {error && <div style={S.err}>{error}</div>}

       {/* Multi-currency notice */}
       {otherCurrencies.length > 0 && (
         <div style={S.multiCurrencyNote}>
           ℹ️ You also have sales in:{" "}
           {otherCurrencies.map((c) => (
             <strong key={c.currency}> {c.currency} {c.grossSales.toFixed(2)}</strong>
           ))}. Showing {currency} below.
         </div>
       )}

       {/* Main Stats Grid */}
       <div className="se-main-grid">
          <div style={S.cardBig}>
             <div style={{ ...S.label, color: "rgba(255,255,255,0.9)" }}>Net Earnings</div>
             <div style={S.value} className="se-big-value">{currency} {total.toFixed(2)}</div>
             <div style={S.sub}>{totalOrders} lifetime orders · {currency} {grossSales.toFixed(2)} gross sales</div>
          </div>
          
          <div style={S.card}>
             <div style={S.label}>Pending</div>
             <div style={S.valueSm}>{currency} {pending.toFixed(2)}</div>
             <div style={{ ...S.sub, color: "#64748b" }}>Orders processing</div>
          </div>

          <div style={S.card}>
             <div style={S.label}>Fulfilled</div>
             <div style={S.valueSm}>${balance.toFixed(2)}</div>
             <div style={{ ...S.sub, color: "#64748b" }}>Shipped / Completed</div>
          </div>

          {/* MSC wallet balance — what the shop has earned in MSC (from MSC-paid
              orders), available to cash out to fiat via the withdrawal page. */}
          <div
             style={{ ...S.card, cursor: "pointer", border: "1px solid rgba(139,92,246,0.5)" }}
             onClick={() => nav("/shop/withdrawal")}
             title="Cash out your MSC balance"
          >
             <div style={S.label}>💎 MSC Balance</div>
             <div style={S.valueSm}>{msc.balanceMsc.toFixed(2)} MSC</div>
             <div style={{ ...S.sub, color: "#a78bfa" }}>
               ≈ {msc.currency} {msc.balanceFiat.toFixed(2)} · Tap to cash out
             </div>
          </div>
       </div>

       {/* Performance Metrics */}
       <div className="se-metrics-grid">
          <div style={S.metricCard}>
             <div style={S.metricIcon}>📅</div>
             <div>
               <div style={S.metricLabel}>Today's Sales</div>
               <div style={S.metricValue}>${todaySales.toFixed(2)}</div>
             </div>
          </div>

          <div style={S.metricCard}>
             <div style={S.metricIcon}>📊</div>
             <div>
               <div style={S.metricLabel}>This Month</div>
               <div style={S.metricValue}>${monthSales.toFixed(2)}</div>
             </div>
          </div>

          <div style={S.metricCard}>
             <div style={S.metricIcon}>📦</div>
             <div>
               <div style={S.metricLabel}>Total Orders</div>
               <div style={S.metricValue}>{totalOrders}</div>
             </div>
          </div>

          <div style={S.metricCard}>
             <div style={S.metricIcon}>💰</div>
             <div>
               <div style={S.metricLabel}>Avg Order Value</div>
               <div style={S.metricValue}>${totalOrders > 0 ? (grossSales / totalOrders).toFixed(2) : '0.00'}</div>
             </div>
          </div>
       </div>

       {/* Send MSC to staff (bonuses / perks from the shop's MSC balance) */}
       <ShopStaffCredit
         balanceMsc={mscLoaded ? msc.balanceMsc : null}
         onBalanceChange={(nb) =>
           setMsc((prev) => ({ ...prev, balanceMsc: nb, balanceFiat: nb * (prev.fxRate || 1) }))
         }
       />

       {/* Status Breakdown */}
       <div style={S.section}>
          <div style={S.h2}>Earnings by Status</div>
          <div className="se-status-grid">
             <div style={S.statusCard}>
               <div style={S.statusLabel}>⏳ Pending</div>
               <div style={S.statusValue}>${(breakdown.pending || 0).toFixed(2)}</div>
             </div>
             <div style={S.statusCard}>
               <div style={S.statusLabel}>✅ Paid</div>
               <div style={S.statusValue}>${(breakdown.paid || 0).toFixed(2)}</div>
             </div>
             <div style={S.statusCard}>
               <div style={S.statusLabel}>🚚 Shipped</div>
               <div style={S.statusValue}>${(breakdown.shipped || 0).toFixed(2)}</div>
             </div>
             <div style={S.statusCard}>
               <div style={S.statusLabel}>🎉 Completed</div>
               <div style={S.statusValue}>${(breakdown.completed || 0).toFixed(2)}</div>
             </div>
          </div>
       </div>

       {/* Recent Orders */}
       <div style={S.section}>
          <div style={S.sectionHeader}>
            <div style={S.h2}>Recent Orders</div>
            <select
              value={txFilter}
              onChange={(e) => setTxFilter(e.target.value)}
              style={S.filterSelect}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="completed">Completed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          {filteredTransactions.length === 0 ? (
             <div style={S.empty}>No orders found.</div>
          ) : (
             <div style={S.list}>
               {filteredTransactions.map((t, i) => {
                 const isOpen = String(openTxId) === String(t._id || i);
                 const txId = t._id || i;
                 return (
                   <div
                     key={txId}
                     style={{ ...S.row, ...(isOpen ? S.rowOpen : null) }}
                     role="button"
                     tabIndex={0}
                     onClick={() => setOpenTxId(isOpen ? null : txId)}
                     onKeyDown={(e) => {
                       if (e.key === "Enter" || e.key === " ") {
                         e.preventDefault();
                         setOpenTxId(isOpen ? null : txId);
                       }
                     }}
                   >
                     <div style={S.rowMain}>
                       <div style={{ flex: 1 }}>
                         <div style={S.tType}>{t.type}</div>
                         <div style={S.tDate}>
                           {new Date(t.createdAt).toLocaleDateString()} · {String(t.status || "").replace(/\b\w/g, (m) => m.toUpperCase())}
                           {t.itemsCount ? ` · ${t.itemsCount} items` : ""}
                         </div>
                       </div>
                       <div style={S.tPos}>+${Number(t.amount || 0).toFixed(2)}</div>
                     </div>

                     {isOpen ? (
                       <div style={S.rowDetails}>
                         <div style={S.detailGrid}>
                           <div style={S.detailItem}>
                             <div style={S.detailLabel}>Order ID</div>
                             <div style={S.detailValue}>{t._id || "—"}</div>
                           </div>
                           <div style={S.detailItem}>
                             <div style={S.detailLabel}>Status</div>
                             <div style={S.detailValue}>{String(t.status || "—")}</div>
                           </div>
                           <div style={S.detailItem}>
                             <div style={S.detailLabel}>Amount</div>
                             <div style={S.detailValue}>{t.currency} {Number(t.amount || 0).toFixed(2)}</div>
                           </div>
                           <div style={S.detailItem}>
                             <div style={S.detailLabel}>Items</div>
                             <div style={S.detailValue}>{t.itemsCount || 0}</div>
                           </div>
                           <div style={S.detailItem}>
                             <div style={S.detailLabel}>Date</div>
                             <div style={S.detailValue}>{new Date(t.createdAt).toLocaleString()}</div>
                           </div>
                           <div style={S.detailItem}>
                            <div style={S.detailLabel}>Buyer</div>
                            <div style={S.detailValue}>{t.customerName || "—"}</div>
                          </div>
                          <div style={S.detailItem}>
                            <div style={S.detailLabel}>Buyer Email</div>
                            <div style={S.detailValue}>{t.customerEmail || "—"}</div>
                           </div>
                          <div style={S.detailItem}>
                            <div style={S.detailLabel}>Return Status</div>
                            <div style={S.detailValue}>{t.returnStatus || "none"}</div>
                          </div>
                          <div style={S.detailItem}>
                            <div style={S.detailLabel}>Return ID</div>
                            <div style={S.detailValue}>{t.returnId || "—"}</div>
                          </div>
                          <div style={S.detailItem}>
                            <div style={S.detailLabel}>Refunded At</div>
                            <div style={S.detailValue}>{t.refundedAt ? new Date(t.refundedAt).toLocaleString() : "—"}</div>
                          </div>
                         </div>
                       </div>
                     ) : null}
                   </div>
                 );
               })}
             </div>
          )}
       </div>
    </div>
  );
}

const S = {
  wrap: { 
    fontFamily: "Arial, sans-serif", 
    background: "var(--bg-primary)",
    minHeight: "100vh",
    color: "var(--text-primary)"
  },
  header: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    marginBottom: 24 
  },
  h1: { 
    fontSize: 28, 
    fontWeight: "900", 
    color: "var(--text-primary)",
    margin: 0
  },
  btnWithdraw: {
    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
    border: "none",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "700",
    fontSize: 14,
    transition: "all 0.2s",
  },
  multiCurrencyNote: {
    padding: "12px 16px",
    background: "rgba(59, 130, 246, 0.1)",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: 10,
    color: "var(--text-secondary)",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  btnRefresh: { 
    background: "var(--btn-secondary-bg)", 
    border: "1.5px solid var(--btn-outline-border)", 
    color: "var(--btn-secondary-text)", 
    padding: "10px 18px", 
    borderRadius: 8, 
    cursor: "pointer",
    fontWeight: "600",
    fontSize: 14,
    transition: "all 0.2s"
  },

  err: { 
    padding: 16, 
    background: "rgba(248, 113, 113, 0.12)", 
    borderRadius: 12, 
    color: "var(--danger)", 
    marginBottom: 16,
    border: "1.5px solid rgba(248, 113, 113, 0.4)",
    fontWeight: "600"
  },

  grid: { 
    display: "grid", 
    gridTemplateColumns: "2fr 1fr 1fr", 
    gap: 16, 
    marginBottom: 24 
  },
  
  cardBig: { 
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", 
    borderRadius: 16, 
    padding: 28, 
    color: "#ffffff",
    boxShadow: "0 8px 24px rgba(59, 130, 246, 0.2)"
  },
  card: { 
    background: "var(--card-bg)", 
    borderRadius: 16, 
    padding: 24, 
    border: "1.5px solid var(--border-color)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  },
  
  label: { 
    fontSize: 12, 
    fontWeight: "700", 
    marginBottom: 10, 
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.8px"
  },
  value: { 
    fontSize: 36, 
    fontWeight: "900", 
    marginBottom: 6,
    color: "#ffffff",
    lineHeight: 1.1
  },
  valueSm: { 
    fontSize: 28, 
    fontWeight: "900", 
    color: "var(--text-primary)", 
    marginBottom: 6,
    lineHeight: 1.1
  },
  sub: { 
    fontSize: 13, 
    color: "rgba(255,255,255,0.95)",
    fontWeight: "500"
  },

  // Performance metrics
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    marginBottom: 24
  },
  metricCard: {
    background: "var(--card-bg)",
    borderRadius: 12,
    padding: 18,
    border: "1.5px solid var(--border-color)",
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  },
  metricIcon: {
    fontSize: 36,
    lineHeight: 1
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "var(--text-secondary)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.8px"
  },
  metricValue: {
    fontSize: 20,
    fontWeight: "900",
    color: "var(--text-primary)",
    lineHeight: 1.2
  },

  // Status breakdown
  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 12
  },
  statusCard: {
    background: "var(--card-bg)",
    borderRadius: 12,
    padding: 18,
    border: "1.5px solid var(--border-color)",
    textAlign: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  },
  statusLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "var(--text-secondary)",
    marginBottom: 10
  },
  statusValue: {
    fontSize: 20,
    fontWeight: "900",
    color: "var(--text-primary)"
  },

  section: { 
    marginTop: 24 
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  filterSelect: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontWeight: 700,
    fontSize: 13,
    outline: "none",
    cursor: "pointer",
  },
  h2: { 
    fontSize: 20, 
    fontWeight: "800", 
    color: "var(--text-primary)", 
    marginBottom: 16,
    margin: "0 0 16px 0"
  },
  empty: { 
    padding: 40, 
    background: "var(--card-bg)", 
    borderRadius: 12, 
    border: "1.5px dashed var(--border-color)", 
    color: "var(--text-secondary)", 
    textAlign: "center",
    fontSize: 14,
    fontWeight: "500"
  },
  
  list: { 
    background: "var(--card-bg)", 
    borderRadius: 12, 
    border: "1.5px solid var(--border-color)", 
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  },
  row: { 
    padding: "18px 20px", 
    borderBottom: "1px solid var(--border-color)", 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    transition: "background 0.2s",
    cursor: "pointer",
    flexDirection: "column",
    gap: 12,
    outline: "none"
  },
  rowMain: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: 12,
  },
  rowOpen: {
    background: "var(--bg-tertiary)",
  },
  rowDetails: {
    width: "100%",
    borderTop: "1px dashed var(--border-color)",
    paddingTop: 12,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  detailItem: {
    background: "var(--card-bg)",
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    padding: 10,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: 6,
  },
  detailValue: {
    fontSize: 13,
    fontWeight: 800,
    color: "var(--text-primary)",
    wordBreak: "break-word",
  },
  tType: { 
    fontWeight: "700", 
    color: "var(--text-primary)",
    fontSize: 15,
    marginBottom: 4
  },
  tDate: { 
    fontSize: 13, 
    color: "var(--text-secondary)",
    fontWeight: "500"
  },
  tPos: { 
    color: "var(--success)", 
    fontWeight: "900",
    fontSize: 17,
    whiteSpace: "nowrap"
  },
  tNeg: { 
    color: "var(--danger)", 
    fontWeight: "900",
    fontSize: 17,
    whiteSpace: "nowrap"
  },
};
